import { useState } from 'react';
import { Home } from 'lucide-react';
import sendEmail from './emailService';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';


const TankMovementForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    deliveryFrom: {
      buildingNumber: '',
      street: '',
      town: '',
      postcode: ''
    },
    deliveryTo: {
      buildingNumber: '',
      street: '',
      town: '',
      postcode: ''
    },
    date: '',
    jobType: '',
    installationType: '',
    typeOfTest: '',
    vessels: [{
      size: '',
      quantity: '1',
      isAboveground: true,
      serialNumber: ''
    }],
    jobDescription: '',
    partsUsed: '',
    tightnessTestComplete: false,
    jobComplete: false
  });

  const jobTypes = [
    'Delivery',
    'New Install',
    'Rollover',
    'Exchange',
    'Uplift'
  ];

  const installationTypes = ['Liquid', 'Vapour', 'Both'];

  const handleVesselChange = (index, field, value) => {
    const newVessels = [...formData.vessels];
    newVessels[index] = { ...newVessels[index], [field]: value };
    setFormData({ ...formData, vessels: newVessels });
  };

  const addVessel = () => {
    setFormData({
      ...formData,
      vessels: [...formData.vessels, { size: '', quantity: '1', isAboveground: true, serialNumber: '' }]
    });
  };

  const removeVessel = (index) => {
    const newVessels = formData.vessels.filter((_, i) => i !== index);
    setFormData({ ...formData, vessels: newVessels });
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting form data:', formData);
      const success = await sendEmail(formData);
      if (success) {
        setShowSuccessModal(true);
      } else {
        console.log('Email send failed');
        alert('Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('An error occurred while submitting the form');
    }
  };

  return (
    <PVSPageLayout className="p-8">
      <div className="max-w-3xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow p-6 border border-pvs-accent/30">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Tank Movement Form</h2>
              <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-TMF-001 | Version: 1.0</p>
            </div>
            <button
              type="button"
              onClick={onBack}
              className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
              title="Return to Forms"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <h3 className="font-medium text-white">Delivery From</h3>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Building Name/Number</label>
                  <input
                    type="text"
                    value={formData.deliveryFrom.buildingNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryFrom: { ...formData.deliveryFrom, buildingNumber: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Street</label>
                  <input
                    type="text"
                    value={formData.deliveryFrom.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryFrom: { ...formData.deliveryFrom, street: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Town</label>
                  <input
                    type="text"
                    value={formData.deliveryFrom.town}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryFrom: { ...formData.deliveryFrom, town: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Postcode</label>
                  <input
                    type="text"
                    value={formData.deliveryFrom.postcode}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryFrom: { ...formData.deliveryFrom, postcode: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-white">Delivery To</h3>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Building Name/Number</label>
                  <input
                    type="text"
                    value={formData.deliveryTo.buildingNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryTo: { ...formData.deliveryTo, buildingNumber: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Street</label>
                  <input
                    type="text"
                    value={formData.deliveryTo.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryTo: { ...formData.deliveryTo, street: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Town</label>
                  <input
                    type="text"
                    value={formData.deliveryTo.town}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryTo: { ...formData.deliveryTo, town: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted">Postcode</label>
                  <input
                    type="text"
                    value={formData.deliveryTo.postcode}
                    onChange={(e) => setFormData({
                      ...formData,
                      deliveryTo: { ...formData.deliveryTo, postcode: e.target.value }
                    })}
                    className="w-full pvs-input"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Date and Job Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full pvs-input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Job Type
                </label>
                <select
                  value={formData.jobType}
                  onChange={(e) => setFormData({ ...formData, jobType: e.target.value })}
                  className="w-full pvs-select"
                  required
                >
                  <option value="">Select job type</option>
                  {jobTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Installation Type and Type of Test */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Installation Type
                </label>
                <select
                  value={formData.installationType}
                  onChange={(e) => setFormData({ ...formData, installationType: e.target.value })}
                  className="w-full pvs-select"
                  required
                >
                  <option value="">Select type</option>
                  {installationTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Type of Test
                </label>
                <input
                  type="text"
                  value={formData.typeOfTest}
                  onChange={(e) => setFormData({ ...formData, typeOfTest: e.target.value })}
                  className="w-full pvs-input"
                />
              </div>
            </div>

            {/* Vessels */}
            {formData.vessels.map((vessel, index) => (
              <div key={index} className="p-4 bg-pvs-card-light rounded-lg border border-pvs-border">
                <div className="flex justify-between items-start">
                  <div className="flex-grow space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                          Vessel Size
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={vessel.size}
                          onChange={(e) => handleVesselChange(index, 'size', e.target.value)}
                          className="w-full pvs-input"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                          Vessel Type
                        </label>
                        <div className="flex space-x-4">
                          <button
                            type="button"
                            onClick={() => handleVesselChange(index, 'isAboveground', true)}
                            className={`px-4 py-2 rounded-md ${
                              vessel.isAboveground
                                ? 'bg-pvs-accent text-pvs-bg'
                                : 'bg-pvs-input-bg text-pvs-text-muted'
                            }`}
                          >
                            Aboveground
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVesselChange(index, 'isAboveground', false)}
                            className={`px-4 py-2 rounded-md ${
                              !vessel.isAboveground
                                ? 'bg-pvs-accent text-pvs-bg'
                                : 'bg-pvs-input-bg text-pvs-text-muted'
                            }`}
                          >
                            Underground
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                          Serial Number
                        </label>
                        <input
                          type="text"
                          value={vessel.serialNumber}
                          onChange={(e) => handleVesselChange(index, 'serialNumber', e.target.value)}
                          className="w-full pvs-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {formData.vessels.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeVessel(index)}
                      className="ml-4 p-1 text-pvs-text-dim hover:text-red-400"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addVessel}
              className="w-full px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
            >
              Add Another Vessel
            </button>

            {/* Job Description and Parts Used */}
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                Job Description
              </label>
              <textarea
                value={formData.jobDescription}
                onChange={(e) => setFormData({ ...formData, jobDescription: e.target.value })}
                className="w-full pvs-input"
                rows="4"  required
                />
              </div>
   
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Parts Used
                </label>
                <textarea
                  value={formData.partsUsed}
                  onChange={(e) => setFormData({ ...formData, partsUsed: e.target.value })}
                  className="w-full pvs-input"
                  rows="2"
                />
              </div>
   
              {/* Test Complete and Job Status */}
              <div className="space-y-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.tightnessTestComplete}
                      onChange={(e) => setFormData({ ...formData, tightnessTestComplete: e.target.checked })}
                      className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
                    />
                    <span className="ml-2 text-pvs-text-muted">Tightness Test Complete</span>
                  </label>
                </div>
   
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.jobComplete}
                      onChange={(e) => setFormData({ ...formData, jobComplete: e.target.checked })}
                      className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded"
                    />
                    <span className="ml-2 text-pvs-text-muted">Job Complete</span>
                  </label>
                </div>
              </div>
   
              {/* Form Actions */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onBack}
                  className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
                >
                  Back to Forms
                </button>
   
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim"
                >
                  Submit Form
                </button>
              </div>
            </form>
          </div>
        </div>
      <FormSuccessModal isOpen={showSuccessModal} formName="Tank Movement Form" onClose={onBack} />
      </PVSPageLayout>
    );
   };
   
   export default TankMovementForm;
